import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes, FaPlus, FaMinus, FaExclamationCircle } from 'react-icons/fa';
import { BASE_URL } from '../../../../BaseUrl';
import { currencySymbolLeft, getSystemSettings } from '../../../../functions/Functions';
import { SettingsContext } from '../../../../contexts/Settings';
import './DigitalMenuItemModal.css';
import { toast } from 'react-toastify';

const VariationOption = React.memo(({ variation, isSelected, onSelect }) => {
  return (
    <div
      className={`dm-modal-option-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(variation)}
    >
      <div className="dm-modal-radio">
        <div className="dm-modal-inner"></div>
      </div>
      <div className="dm-modal-option-details">
        <span className="dm-modal-option-name">{variation.variation_name}</span>
        <span className="dm-modal-option-price">{currencySymbolLeft()} {variation.food_with_variation_price}</span>
      </div>
    </div>
  );
});

const PropertyItem = React.memo(({
  groupId, prop, isMultiple, limit, isSelected, qty, allowMultiQty,
  isDisabled, isShowTags, renderFoodQty, onToggle, onQtyChange
}) => {
  return (
    <div
      className={`dm-modal-list-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
      onClick={() => {
        if (!allowMultiQty) onToggle(groupId, prop, isMultiple, limit);
        else if (!isSelected) onQtyChange({ stopPropagation: () => { } }, groupId, prop, limit, true);
      }}
    >
      <div className="dm-modal-list-item-left">
        {!allowMultiQty && (
          <div className={isMultiple ? "dm-modal-checkbox" : "dm-modal-radio"}>
            <div className="dm-modal-inner"></div>
          </div>
        )}
        <div className="d-flex flex-column position-relative">
          {isShowTags(prop) && renderFoodQty(prop)}
          <span className="dm-modal-prop-name">{prop.name || prop.property}</span>
        </div>
      </div>

      <div className="dm-modal-list-item-right d-flex align-items-center">
        {parseFloat(prop.extra_price || prop.price_per_qty || 0) >= 0 && (
          <span className="dm-modal-prop-price mr-3">+ {currencySymbolLeft()} {parseFloat(prop.extra_price || prop.price_per_qty || 0).toFixed(2)}</span>
        )}

        {allowMultiQty && isSelected && (
          <div className="dm-prop-qty-selector ml-2">
            <button
              className="dm-prop-qty-btn"
              onClick={(e) => onQtyChange(e, groupId, prop, limit, false)}
            ><FaMinus /></button>
            <span className="dm-prop-qty-text">{qty}</span>
            <button
              className="dm-prop-qty-btn"
              onClick={(e) => onQtyChange(e, groupId, prop, limit, true)}
            ><FaPlus /></button>
          </div>
        )}
      </div>
    </div>
  );
});

const PropertyGroup = React.memo(({ groupId, propsInGroup, validation, selectedProperties, handleDisable, isShowTags, renderFoodQty, handlePropertyToggle, handlePropertyQtyChange }) => {
  const isRequired = validation.required === 1;
  const isMultiple = validation.multi_select === "multiple";
  const limit = validation.limit || 0;

  const groupName = validation?.property_group_name || propsInGroup[0]?.property_group_name || `Select Properties`;

  return (
    <div id={`prop-group-${groupId}`} className="dm-modal-section">
      <div className="dm-modal-section-header">
        <h6 className="dm-modal-section-title">{groupName}</h6>
        <div className="dm-modal-section-badges d-flex align-items-center flex-wrap">
          {isRequired && <span className="dm-badge-required d-flex align-items-center"><FaExclamationCircle className="dm-badge-icon" /> Required</span>}
          {!isRequired && <span className="dm-badge-optional d-flex align-items-center"><FaExclamationCircle className="dm-badge-icon" /> Optional</span>}
          {limit > 0 && <span className="dm-badge-limit d-flex align-items-center"><FaExclamationCircle className="dm-badge-icon" /> Select {limit}</span>}
        </div>
      </div>

      <div className="dm-modal-list">
        {propsInGroup.map(prop => {
          const isSelected = selectedProperties[groupId] && selectedProperties[groupId][prop.id];
          const isDisabled = handleDisable(prop);
          const allowMultiQty = prop.allow_multi_quantity === 1 || prop.allow_multi_quantity === "1";
          const qty = isSelected ? selectedProperties[groupId][prop.id].quantity : 0;

          return (
            <PropertyItem
              key={prop.id}
              groupId={groupId}
              prop={prop}
              isMultiple={isMultiple}
              limit={limit}
              isSelected={!!isSelected}
              qty={qty}
              allowMultiQty={allowMultiQty}
              isDisabled={isDisabled}
              isShowTags={isShowTags}
              renderFoodQty={renderFoodQty}
              onToggle={handlePropertyToggle}
              onQtyChange={handlePropertyQtyChange}
            />
          );
        })}
      </div>
    </div>
  );
});

const getParsedProperties = (currentItem) => {
  let parsedProps = {};
  if ((currentItem?.has_property === "1" || currentItem?.has_property === 1) && currentItem?.properties) {
    if (Array.isArray(currentItem.properties)) {
      currentItem.properties.forEach(propArray => {
        if (propArray && propArray.length > 0) {
          let groupId = null;
          try {
            groupId = JSON.parse(propArray[0].property_group_id)[0];
          } catch (e) {
            groupId = propArray[0].property_group_id.replace(/[\[\]]/g, "");
          }
          parsedProps[groupId] = propArray;
        }
      });
    } else {
      for (const key in currentItem.properties) {
        parsedProps[key] = currentItem.properties[key];
      }
    }
  }
  return parsedProps;
};

const DigitalMenuItemModal = ({ isOpen, onClose, item, onAddToCart, checkIsPunchingEnabled, showImage = true }) => {
  const { generalSettings } = useContext(SettingsContext);
  
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState({});
  const [parsedPropertyGroups, setParsedPropertyGroups] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [comments, setComments] = useState("");

  useEffect(() => {
    if (isOpen && item) {
      setQuantity(1);
      setComments("");
      if (item.has_variation === "1" || item.has_variation === 1) {
        if (checkIsPunchingEnabled && item.variations && item.variations.length > 0) {
          setSelectedVariation(item.variations[0]);
        }
      } else {
        setSelectedVariation(null);
      }
      setSelectedProperties({});
      
      const parsedProps = getParsedProperties(item);
      setParsedPropertyGroups(parsedProps);
    }
  }, [isOpen, item, checkIsPunchingEnabled]);

  const totalPrice = useMemo(() => {
    if (!item) return 0;
    const basePrice = parseFloat(selectedVariation ? selectedVariation.food_with_variation_price : item.price);
    return basePrice * quantity;
  }, [item, selectedVariation, quantity]);

  const handleDisable = useCallback((prpItem) => {
    const isDisabled = !!((generalSettings && !prpItem?.food_with_variation_id &&
      parseInt(getSystemSettings(generalSettings, "fgs_enable")) === 1 &&
      (prpItem?.prop_qty <= 0 || !prpItem?.prop_qty) && !prpItem?.is_recipe));
    return isDisabled;
  }, [generalSettings]);

  const isShowTags = useCallback((prpItem) => {
    return (
      generalSettings &&
      parseInt(getSystemSettings(generalSettings, "fgs_enable")) === 1 &&
      !prpItem?.food_with_variation_id && !prpItem?.is_recipe
    );
  }, [generalSettings]);

  const renderFoodQty = useCallback((prpItem) => {
    if (!prpItem) return null;
    return (
      prpItem?.prop_qty <= 0 || prpItem?.prop_qty === undefined ?
        <span className="dm-prp-stock dm-stock-out">Out of stock</span> :
        <span className="dm-prp-stock dm-stock-in">Qty: {prpItem?.prop_qty}</span>
    );
  }, []);

  const handleVariationSelect = useCallback((variation) => {
    if(!checkIsPunchingEnabled){
      return;
    }
    setSelectedVariation(variation);
    setSelectedProperties({});
  }, [checkIsPunchingEnabled]);

  const handlePropertyToggle = useCallback((group, propertyItem, isMultiple, limit) => {
    if(!checkIsPunchingEnabled){
      return;
    }
    if (handleDisable(propertyItem)) return;

    setSelectedProperties(prev => {
      const newState = { ...prev };
      const groupId = group.id || group;
      const propId = propertyItem.id;

      newState[groupId] = { ...(newState[groupId] || {}) };
      const currentGroupSelections = newState[groupId];

      if (currentGroupSelections[propId]) {
        delete currentGroupSelections[propId];
      } else {
        if (!isMultiple) {
          newState[groupId] = {
            [propId]: { ...propertyItem, quantity: 1 }
          };
        } else {
          let currentCount = 0;
          Object.values(currentGroupSelections).forEach(p => currentCount += p.quantity);
          if (limit > 0 && currentCount >= limit) {
            toast.error(`You can only select up to ${limit} items.`);
            return prev;
          }
          currentGroupSelections[propId] = { ...propertyItem, quantity: 1 };
        }
      }
      return newState;
    });
  }, [handleDisable, checkIsPunchingEnabled]);

  const handlePropertyQtyChange = useCallback((e, group, propertyItem, limit, increment) => {
    if (e && e.stopPropagation) e.stopPropagation();
     if(!checkIsPunchingEnabled){
      return;
    }
    if (handleDisable(propertyItem)) return;

    setSelectedProperties(prev => {
      const newState = { ...prev };
      const groupId = group.id || group;
      const propId = propertyItem.id;

      newState[groupId] = { ...(newState[groupId] || {}) };
      const currentGroupSelections = newState[groupId];

      const existingProp = currentGroupSelections[propId];
      let currentQty = existingProp ? existingProp.quantity : 0;

      if (increment) {
        let currentGroupTotalCount = 0;
        Object.values(currentGroupSelections).forEach(p => currentGroupTotalCount += p.quantity);

        if (limit > 0 && currentGroupTotalCount >= limit) {
          toast.error(`You can only select up to ${limit} items.`);
          return prev;
        }
        currentQty += 1;
      } else {
        currentQty -= 1;
      }

      if (currentQty <= 0) {
        delete currentGroupSelections[propId];
      } else {
        currentGroupSelections[propId] = { ...propertyItem, quantity: currentQty };
      }

      return newState;
    });
  }, [handleDisable, checkIsPunchingEnabled]);

  const incrementQuantity = () => setQuantity(q => q + 1);
  const decrementQuantity = () => setQuantity(q => (q > 1 ? q - 1 : 1));

  const getGroupName = (groupId) => {
    if (item?.property_groups_validation?.[groupId]?.property_group_name) {
      return item.property_groups_validation[groupId].property_group_name;
    }
    return `Group ${groupId}`;
  };

  const validateAndSubmit = () => {
    if ((item.has_variation === "1" || item.has_variation === 1) && !selectedVariation) {
      toast.error("Please select a variation.");
      return;
    }

    if (item.has_property === "1" || item.has_property === 1) {
      const validations = item.property_groups_validation || {};
      for (const groupId in validations) {
        const validation = validations[groupId];
        if (validation.is_variation && selectedVariation) {
          try {
            const validVariations = JSON.parse(validation.variation_id);
            if (!validVariations.includes(parseInt(selectedVariation.variation_id))) continue;
          } catch (e) { }
        }

        if (validation.required === 1) {
          const groupProps = selectedProperties[groupId];
          let selectedInGroup = 0;
          if (groupProps) {
            Object.values(groupProps).forEach(p => selectedInGroup += p.quantity);
          }

          const groupName = getGroupName(groupId);
          let errorMsg = null;

          if (validation.multi_select === "multiple") {
            if (validation.limit > 0 && selectedInGroup !== validation.limit) {
              errorMsg = `You have to pick at least ${validation.limit} item from ${groupName}.`;
            } else if (validation.limit == 0 && selectedInGroup <= 0) {
              errorMsg = `You have to pick at least one item from ${groupName}.`;
            }
          } else {
            if (selectedInGroup <= 0) {
              errorMsg = `You have to pick at least one item from ${groupName}.`;
            }
          }

          if (errorMsg) {
            toast.error(errorMsg, { whiteSpace: "pre-line" });
            const element = document.getElementById(`prop-group-${groupId}`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
          }
        }
      }
    }

    const payloadProperties = Object.keys(selectedProperties).length ? Object.values(selectedProperties).flatMap(Object.values) : [];

    onAddToCart(item, selectedVariation, payloadProperties, quantity, comments);
    onClose();
  };

  const renderVariationsSection = () => {
    if (!(item?.has_variation === "1" || item?.has_variation === 1) || !item?.variations) return null;
    return (
      <div className="dm-modal-section">
        <h6 className="dm-modal-section-title">CHOOSE AN OPTION <span className="text-danger">*</span></h6>
        <div className="dm-modal-options-grid">
          {item.variations.map(variation => (
            <VariationOption
              key={variation.food_with_variation_id}
              variation={variation}
              isSelected={selectedVariation?.food_with_variation_id === variation.food_with_variation_id}
              onSelect={handleVariationSelect}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderPropertiesSection = () => {
    if (Object.keys(parsedPropertyGroups).length === 0) return null;

    const validations = item?.property_groups_validation || {};

    return Object.keys(parsedPropertyGroups).map(groupId => {
      const validation = validations[groupId] || {};
      if (validation.is_variation && selectedVariation) {
        try {
          const validVariations = JSON.parse(validation.variation_id);
          if (!validVariations.includes(parseInt(selectedVariation.variation_id))) {
            return null;
          }
        } catch (e) { }
      }
      return (
        <PropertyGroup
          key={groupId}
          groupId={groupId}
          propsInGroup={parsedPropertyGroups[groupId]}
          validation={validation}
          selectedProperties={selectedProperties}
          handleDisable={handleDisable}
          isShowTags={isShowTags}
          renderFoodQty={renderFoodQty}
          handlePropertyToggle={handlePropertyToggle}
          handlePropertyQtyChange={handlePropertyQtyChange}
        />
      );
    });
  };

  if (!isOpen || !item) return null;

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg" dialogClassName="dm-item-modal">
      <div className="dm-modal-content-wrapper">
        <button className="dm-modal-close-btn" onClick={onClose}>
          <FaTimes />
        </button>
        <div className="dm-modal-layout">
          {/* Hide the image column entirely when the branch disables menu images. */}
          {showImage ? <div className="dm-modal-image-col">
            <img
              src={item.image ? BASE_URL + item.image : "/assets/img/default-item.jpeg"}
              alt={item.name}
              className="dm-modal-image"
              onError={(e) => { e.target.onerror = null; e.target.src = "/assets/img/default-item.jpeg" }}
            />
            {/* Gradient Overlay for Text Readability */}
            <div className="dm-modal-image-overlay"></div>
            {/* Absolute positioned Header */}
            <div className="dm-modal-header-absolute px-4 pb-3">
              <div className="dm-modal-title-overlay">{item.name}</div>
            </div>
          </div> : ""}
          <div className="dm-modal-details-col">
            <div className="dm-modal-scrollable-area">
              {!showImage && <div className="dm-modal-title-overlay" style={{ color: "#2c2c2c", textShadow: "none" }}>{item.name}</div>}
              <h4 className="dm-modal-price my-2">{currencySymbolLeft()} {item.price}</h4>
              {item?.description && (
                <div className="text-preline dm-modal-description mb-3">
                  <p>{item.description}</p>
                </div>
              )}
              {renderVariationsSection()}
              {renderPropertiesSection()}

              {/* Special Instructions */}
              {
                checkIsPunchingEnabled && (
                  <div className="dm-modal-section mt-4 mb-2">
                    <div className="dm-modal-section-header">
                      <h6 className="dm-modal-section-title">Special Instructions</h6>
                      <div className="dm-modal-section-badges">
                        <span className="dm-badge-optional">Optional</span>
                      </div>
                    </div>
                    <textarea
                      className="dm-instruction-textarea shadow-sm"
                      rows="3"
                      placeholder="Type here..."
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                    ></textarea>
                  </div>
                )
              }
            </div>
            {
              checkIsPunchingEnabled && (
                <div className="dm-modal-footer-actions">
                  <div className="dm-modal-qty-selector">
                    <button className="dm-qty-btn" onClick={decrementQuantity}><FaMinus /></button>
                    <span className="dm-qty-text">{quantity}</span>
                    <button className="dm-qty-btn" onClick={incrementQuantity}><FaPlus /></button>
                  </div>
                  <button className="dm-modal-add-btn" onClick={validateAndSubmit}>
                    <span>{currencySymbolLeft()} {totalPrice.toFixed(2)}</span>
                    <span className="dm-btn-separator">|</span>
                    <span>Add to Cart</span>
                  </button>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(DigitalMenuItemModal);
